import { classify } from "~/features/audience"
import { formatAuthors, formatDate } from "~/shared/lib/formatting"
import { readerUrl } from "./readerUrl"
import type { DigitalEventAsset, EbookAsset, EbookLinkIdKind, LibraryCardItem, StudyAsset } from "./types"

const UNTITLED = "Untitled"

export const toStudyCard = (asset: StudyAsset): LibraryCardItem => ({
  id: asset.id,
  title: asset.title ?? UNTITLED,
  audience: classify({ title: asset.title, ministryArea: asset.ministryArea }),
  meta: formatAuthors(asset.authors),
  imageUrl: asset.images?.thumbnailUrl ?? asset.images?.posterUrl ?? undefined,
  href: `/study/${asset.id}`,
  isExternal: false,
})

/** The two identifiers an ebook can be reached by. Either may be absent. */
export interface EbookIdentifiers {
  /** The content GUID on `Ebook.id`. Absent when the card did not come from `libraryV2`. */
  readonly id: string | undefined
  /** The 9-digit Lifeway Item Number, off the matched commerce seat. */
  readonly lin: string | undefined
}

/**
 * Aggregate id first, LIN as the fallback.
 *
 * Both halves are load-bearing and cover opposite cases. A `libraryV2` card
 * whose title no seat claims keeps its aggregate id rather than linking nowhere.
 * A card built FROM a seat has no aggregate id at all — the missing GUID is the
 * whole reason it exists (`orphanEntitlements.ts`) — so it takes the LIN.
 */
export const ebookHref = ({ id, lin }: EbookIdentifiers): Pick<LibraryCardItem, "href" | "linkIdKind"> => {
  const preference: readonly (readonly [EbookLinkIdKind, string | undefined])[] = [
    ["aggregate", id],
    ["lin", lin],
  ]

  for (const [kind, value] of preference) {
    if (value) {
      return { href: readerUrl(value), linkIdKind: kind }
    }
  }

  return { href: undefined, linkIdKind: undefined }
}

/** An untitled ebook has nothing to match a seat on, so it never gets a LIN. */
export const toEbookCard = (
  asset: EbookAsset,
  linFor: (title: string) => string | undefined = () => undefined,
): LibraryCardItem => ({
  id: asset.id,
  title: asset.title ?? UNTITLED,
  audience: classify({ title: asset.title }),
  meta: undefined,
  imageUrl: asset.cover ?? undefined,
  isExternal: true,
  ...ebookHref({ id: asset.id, lin: asset.title ? linFor(asset.title) : undefined }),
})

export const toDigitalEventCard = (asset: DigitalEventAsset): LibraryCardItem => ({
  id: asset.id,
  title: asset.name ?? UNTITLED,
  audience: classify({ title: asset.name, ministryArea: asset.ministryArea }),
  meta: undefined,
  dateChip: formatDate(asset.startDate),
  imageUrl: asset.thumbnailUrl ?? asset.posterUrl ?? undefined,
  href: `/event/${asset.id}`,
  isExternal: false,
})
